/**
 * Pure utility functions extracted from ShipDatePayment for testability.
 *
 * MONEY SAFETY: Silent bugs here cause the DB to not reflect payments —
 * customer pays but system shows unpaid (or vice versa). Every function
 * here has a corresponding test in __tests__/paymentFormUtils.test.js.
 */

/**
 * Appends order receipt entries to multipart FormData.
 * CRITICAL: weightPriceSelected must produce an 'orderReceipts' entry
 * with amountType='weightPaid' — the backend switch uses this exact string.
 */
export function appendOrderData(formData, rowData) {
  rowData.forEach(order => {
    if (order.amountSelected === 'half') {
      formData.append('orderReceipts', JSON.stringify({
        id: order.id,
        amountType: 'productPaymentFirstHalf',
      }));
    } else if (order.amountSelected === 'full') {
      formData.append('orderReceipts', JSON.stringify({
        id: order.id,
        amountType: order.productPayment === 'มัดจำ 50%' ? 'productPaymentSecondHalf' : 'productPayment',
      }));
    }

    if (order.deliveryFeeJpSelected === 'half') {
      formData.append('orderReceipts', JSON.stringify({
        id: order.id,
        amountType: 'shipJpFirstHalf',
      }));
    } else if (order.deliveryFeeJpSelected === 'full') {
      formData.append('orderReceipts', JSON.stringify({
        id: order.id,
        amountType: order.shipJpPaid === 'มัดจำ 50%' ? 'shipJpSecondHalf' : 'shipJpPaid',
      }));
    }

    if (order.weightPriceSelected) {
      formData.append('orderReceipts', JSON.stringify({
        id: order.id,
        amountType: 'weightPaid',
      }));
    }

    if (order.deliveryFeeThSelected) {
      formData.append('orderReceipts', JSON.stringify({
        id: order.id,
        amountType: 'shipThPaid',
      }));
    }
  });
  return formData;
}

/**
 * Builds the orderReceipts array for non-company code generation.
 * This is what gets saved in OrderSummaryCode.orderReceipts and later
 * used by handleCodeLookup to restore selections.
 */
export function buildOrderReceipts(rowData) {
  const orderReceipts = [];
  rowData.forEach(order => {
    if (order.amountSelected === 'half') {
      orderReceipts.push({ id: order.id, amountType: 'productPaymentFirstHalf' });
    } else if (order.amountSelected === 'full') {
      orderReceipts.push({ id: order.id, amountType: 'productPayment' });
    }
    if (order.deliveryFeeJpSelected === 'half') {
      orderReceipts.push({ id: order.id, amountType: 'shipJpFirstHalf' });
    } else if (order.deliveryFeeJpSelected === 'full') {
      orderReceipts.push({ id: order.id, amountType: 'shipJpPaid' });
    }
    if (order.deliveryFeeThSelected) {
      orderReceipts.push({ id: order.id, amountType: 'shipThPaid' });
    }
    if (order.weightPriceSelected) {
      orderReceipts.push({ id: order.id, amountType: 'weightPaid' });
    }
  });
  return orderReceipts;
}

/**
 * Returns true if ANY row has a payment selection.
 * Guards against submitting an empty payment (no rows selected) which
 * previously caused a silent failure — receipt uploaded but no orders updated.
 */
export function hasAnySelection(rowData) {
  return rowData.some(order =>
    order.amountSelected !== 'none' ||
    order.deliveryFeeJpSelected !== 'none' ||
    order.weightPriceSelected ||
    order.deliveryFeeThSelected
  );
}

/**
 * Returns true if any SELECTED field has a null amount (invalid state).
 * This indicates the admin toggled a field that has no price value entered.
 * Weight uses OR — either weightPrice OR weight is sufficient.
 */
export function hasNullAmountSelected(rowData) {
  return rowData.some(order =>
    ((order.amount == null) && (order.amountSelected === 'half' || order.amountSelected === 'full')) ||
    ((order.deliveryFeeJp == null) && (order.deliveryFeeJpSelected === 'half' || order.deliveryFeeJpSelected === 'full')) ||
    ((order.deliveryFeeTh == null) && order.deliveryFeeThSelected) ||
    ((order.weightPrice == null && order.weight == null) && order.weightPriceSelected)
  );
}

/**
 * Applies "Toggle Column" logic for weightPriceSelected.
 *
 * NULL GUARD: Orders with no weight data (weightPrice=null AND weight=null)
 * are NEVER touched. Without this guard, toggling selects null-weight orders,
 * hasNullAmountSelected fires, and the entire payment is blocked — forcing the
 * admin to manually deselect, which can accidentally clear weight-eligible rows.
 */
export function applyWeightColumnToggle(currentData) {
  const anyNotSelected = currentData.some(item =>
    !item.weightPaid &&
    (item.weightPrice != null || item.weight != null) &&
    !item.weightPriceSelected
  );
  return currentData.map(item => {
    if (item.weightPaid) return item;
    if (item.weightPrice == null && item.weight == null) return item;
    return { ...item, weightPriceSelected: anyNotSelected };
  });
}

/**
 * Applies "Toggle Column" logic for deliveryFeeThSelected.
 * Orders without a Thai delivery fee (null) are NEVER touched.
 */
export function applyDeliveryFeeThColumnToggle(currentData) {
  const anyNotSelected = currentData.some(item =>
    !item.shipThPaid &&
    item.deliveryFeeTh != null &&
    !item.deliveryFeeThSelected
  );
  return currentData.map(item => {
    if (item.shipThPaid) return item;
    if (item.deliveryFeeTh == null) return item;
    return { ...item, deliveryFeeThSelected: anyNotSelected };
  });
}

/**
 * Maps saved orderReceipts (from OrderSummaryCode) back to rowData selections.
 * Used by handleCodeLookup to restore the selection state from a payment code.
 */
export function buildSelectionMapFromReceipts(orderReceipts) {
  const selectionMap = {};
  orderReceipts.forEach(({ id, amountType }) => {
    if (!selectionMap[id]) selectionMap[id] = {};
    if (amountType === 'productPaymentFirstHalf') selectionMap[id].amountSelected = 'half';
    else if (amountType === 'productPayment' || amountType === 'productPaymentSecondHalf') selectionMap[id].amountSelected = 'full';
    else if (amountType === 'shipJpFirstHalf') selectionMap[id].deliveryFeeJpSelected = 'half';
    else if (amountType === 'shipJpPaid' || amountType === 'shipJpSecondHalf') selectionMap[id].deliveryFeeJpSelected = 'full';
    else if (amountType === 'weightPaid') selectionMap[id].weightPriceSelected = true;
    else if (amountType === 'shipThPaid') selectionMap[id].deliveryFeeThSelected = true;
  });
  return selectionMap;
}

/**
 * Applies a selectionMap (from code lookup) to a rowData array.
 * Returns a new array with selections applied, leaving unmatched rows unchanged.
 */
export function applySelectionMap(rowData, selectionMap) {
  return rowData.map(item => {
    const sel = selectionMap[item.id];
    if (!sel) return item;
    return {
      ...item,
      ...(sel.amountSelected !== undefined && { amountSelected: sel.amountSelected }),
      ...(sel.deliveryFeeJpSelected !== undefined && { deliveryFeeJpSelected: sel.deliveryFeeJpSelected }),
      ...(sel.weightPriceSelected !== undefined && { weightPriceSelected: sel.weightPriceSelected }),
      ...(sel.deliveryFeeThSelected !== undefined && { deliveryFeeThSelected: sel.deliveryFeeThSelected }),
    };
  });
}
